import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, Minus, Check } from 'lucide-react';
import { animalData } from '../../data/animals';
import { useCart } from '../../context/CartContext';
import { toast } from 'sonner';

const fmt = (p) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: p % 1 === 0 ? 0 : 2 }).format(p);

// Tiered pricing: 1st = $3,500, 2nd = $1,995, 3rd = $1,500
const TIER_PRICES = [3500, 1995, 1500];

export function BigCatsSection() {
  const { addItem } = useCart();
  // Track quantity per cat: { 'bengal-tiger': 0, 'white-tiger': 0, 'male-lion': 0 }
  const [quantities, setQuantities] = useState(
    Object.fromEntries(animalData.bigCats.map(c => [c.id, 0]))
  );
  const [includeArena, setIncludeArena] = useState(false);

  const totalCount = Object.values(quantities).reduce((s, q) => s + q, 0);
  const totalPrice = TIER_PRICES.slice(0, totalCount).reduce((s, p) => s + p, 0);

  const arena = animalData.bigCatPricing.find(p => p.id === 'steel-arena');

  const increment = (catId) => {
    if (totalCount >= 3) return;
    const cat = animalData.bigCats.find(c => c.id === catId);
    const maxQty = cat?.maxQty || 1;
    if (quantities[catId] >= maxQty) return;
    setQuantities(prev => ({ ...prev, [catId]: prev[catId] + 1 }));
  };

  const decrement = (catId) => {
    if (quantities[catId] <= 0) return;
    setQuantities(prev => ({ ...prev, [catId]: prev[catId] - 1 }));
  };

  const handleAdd = () => {
    if (totalCount === 0) return;
    let tierIndex = 0;
    animalData.bigCats.forEach(cat => {
      const qty = quantities[cat.id];
      for (let i = 0; i < qty; i++) {
        const tierLabel = tierIndex === 0 ? '1st' : tierIndex === 1 ? '2nd' : '3rd';
        addItem({
          id: `${cat.id}-${Date.now()}-${tierIndex}`,
          name: `Big Cat — ${cat.name} (${tierLabel})`,
          price: TIER_PRICES[tierIndex],
          category: 'big-cats',
          image: cat.image,
          hours: 1,
        });
        tierIndex++;
      }
    });
    if (includeArena && arena) {
      addItem({ id: `steel-arena-${Date.now()}`, name: arena.name, description: arena.description, price: arena.price, category: 'big-cats', hours: 1 });
    }
    toast.success(`${totalCount} Big Cat${totalCount > 1 ? 's' : ''}${includeArena ? ' + Steel Arena' : ''} added to estimate`);
    setQuantities(Object.fromEntries(animalData.bigCats.map(c => [c.id, 0])));
    setIncludeArena(false);
  };

  return (
    <section data-testid="big-cats-section" className="py-24 md:py-32 px-6 md:px-12 bg-surface relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <img src="https://images.pexels.com/photos/2220337/pexels-photo-2220337.jpeg?auto=compress&cs=tinysrgb&w=1920" alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-surface via-surface to-surface/80" />
      </div>

      <div className="max-w-[1400px] mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <p className="text-xs tracking-[0.3em] uppercase text-gold mb-4">For Display or Private Photoshoots</p>
          <h2 className="font-display text-4xl md:text-6xl text-white uppercase tracking-wide mb-4">Big Cats</h2>
          <p className="text-white/50 max-w-2xl text-sm md:text-base leading-relaxed">
            Professional display in 6'x8' steel enclosure or 20' arena with licensed handler. Up to 3 hours on site for events. Private photo-shoots out of cage with conditions met.
          </p>
        </motion.div>

        {/* Big Cat Cards with Quantity Selectors */}
        <div className="grid grid-cols-3 gap-4 md:gap-6 mb-8">
          {animalData.bigCats.map((cat, i) => {
            const qty = quantities[cat.id];
            const maxQty = cat.maxQty || 1;
            const canIncrement = qty < maxQty && totalCount < 3;

            return (
              <motion.div
                key={cat.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.15 }}
                className={`group transition-all duration-300 ${qty > 0 ? 'ring-2 ring-gold' : ''}`}
                data-testid={`select-cat-${cat.id}`}
              >
                <div className="relative aspect-[3/4] overflow-hidden">
                  <img src={cat.image} alt={cat.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />

                  {/* Cat name */}
                  <p className="absolute bottom-4 left-4 font-display text-xl md:text-2xl text-white">{cat.name}</p>

                  {/* Availability note */}
                  {maxQty > 1 && (
                    <div className="absolute top-4 left-4 bg-background/80 backdrop-blur-sm px-2.5 py-1 border border-white/10">
                      <span className="text-white/60 text-[10px] uppercase tracking-wider">{maxQty} available</span>
                    </div>
                  )}

                  {/* Selected badge */}
                  {qty > 0 && (
                    <div className="absolute top-4 right-4 w-8 h-8 bg-gold flex items-center justify-center rounded-full">
                      <span className="text-background text-sm font-bold">{qty}</span>
                    </div>
                  )}
                </div>

                {/* Quantity controls */}
                <div className="flex items-center justify-center gap-3 py-3 bg-background/60 border-t border-white/5">
                  <button
                    onClick={() => decrement(cat.id)}
                    disabled={qty === 0}
                    className={`w-9 h-9 flex items-center justify-center border transition-colors ${
                      qty > 0 ? 'border-white/20 text-white hover:bg-white/10' : 'border-white/5 text-white/15 cursor-not-allowed'
                    }`}
                    data-testid={`minus-${cat.id}`}
                  >
                    <Minus className="w-3.5 h-3.5" />
                  </button>
                  <span className="text-white text-lg font-medium w-6 text-center" data-testid={`qty-${cat.id}`}>{qty}</span>
                  <button
                    onClick={() => increment(cat.id)}
                    disabled={!canIncrement}
                    className={`w-9 h-9 flex items-center justify-center border transition-colors ${
                      canIncrement ? 'border-gold/40 text-gold hover:bg-gold/10' : 'border-white/5 text-white/15 cursor-not-allowed'
                    }`}
                    data-testid={`plus-${cat.id}`}
                  >
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Pricing Summary + Add Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          {/* Tier indicators */}
          <div className="flex flex-wrap gap-3 items-center mb-6">
            <div className="flex gap-3">
              {TIER_PRICES.map((price, i) => {
                const labels = ['1st Big Cat', '2nd Big Cat', '3rd Big Cat'];
                const active = i < totalCount;
                return (
                  <div
                    key={`tier-${price}`}
                    className={`px-4 py-2.5 border transition-all ${active ? 'border-gold bg-gold/10 text-gold' : 'border-white/10 bg-background/30 text-white/30'}`}
                    data-testid={`tier-${i + 1}`}
                  >
                    <span className="text-xs font-medium">{labels[i]}</span>
                    <span className="text-sm font-medium ml-2">{fmt(price)}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Steel Arena Toggle */}
          {arena && (
            <div
              className={`p-5 border flex justify-between items-center mb-6 cursor-pointer transition-all ${
                includeArena ? 'border-gold bg-gold/5' : 'border-white/10 bg-background/30'
              }`}
              onClick={() => setIncludeArena(!includeArena)}
              data-testid="arena-toggle"
            >
              <div>
                <h4 className="text-white font-medium text-sm">{arena.name}</h4>
                <p className="text-white/40 text-xs">{arena.description}</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-gold font-medium">{fmt(arena.price)}</span>
                <div className={`w-6 h-6 border-2 flex items-center justify-center transition-all ${
                  includeArena ? 'border-gold bg-gold' : 'border-white/20'
                }`}>
                  {includeArena && <Check className="w-3.5 h-3.5 text-background" />}
                </div>
              </div>
            </div>
          )}

          {/* Add to Estimate Button */}
          <button
            onClick={handleAdd}
            disabled={totalCount === 0}
            className={`w-full md:w-auto px-8 py-4 text-sm font-semibold uppercase tracking-wide transition-colors duration-300 ${
              totalCount > 0
                ? 'bg-gold text-background hover:bg-gold-light'
                : 'bg-white/10 text-white/30 cursor-not-allowed'
            }`}
            data-testid="add-big-cats"
          >
            <Plus className="w-3.5 h-3.5 inline mr-2" />
            {totalCount === 0
              ? 'Select Big Cats Above'
              : `Add ${totalCount} Big Cat${totalCount > 1 ? 's' : ''}${includeArena ? ' + Arena' : ''} — ${fmt(totalPrice + (includeArena ? arena.price : 0))}`
            }
          </button>

          <div className="p-4 bg-background/50 border-l-2 border-gold/50 mt-6">
            <p className="text-white/40 text-xs">
              Maximum 3 big cats per event. Display time: up to 3 hours. No guest contact with big cats. Requires flat surfaces, 6' clearance, truck & trailer remain on-site.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
